class ZerokitProductBuy {
    constructor(section) {
        this.section = section;

        /*
         * Gallery must remain independent from product-form functionality.
         * Initialize it first so media interaction still works even if the
         * product form or variant JSON becomes unavailable.
         */
        this.initGallery();

        this.form = section.querySelector('[data-zk-buy-form]');
        this.variantInput = section.querySelector('[data-zk-buy-variant-id]');

        this.currentPrice = section.querySelector(
            '[data-zk-buy-current-price]'
        );

        this.comparePrice = section.querySelector(
            '[data-zk-buy-compare-price]'
        );

        this.status = section.querySelector('[data-zk-buy-status]');
        this.addButton = section.querySelector('[data-zk-buy-add]');
        this.addLabel = section.querySelector('[data-zk-buy-add-label]');

        this.optionInputs = Array.from(
            section.querySelectorAll('[data-zk-buy-option]')
        );

        const variantData = section.querySelector(
            '[data-zk-buy-variants]'
        );

        /*
         * Stop only variant/form initialization here.
         * The gallery has already been initialized above.
         */
        if (!this.form || !variantData) {
            return;
        }

        try {
            this.variants = JSON.parse(variantData.textContent);
        } catch (error) {
            console.error(
                'Zerokit: Unable to parse product variant data.',
                error
            );

            this.variants = [];
            return;
        }

        this.bindVariantEvents();
    }


    /* ==========================================================================
       Variants
       ========================================================================== */

    bindVariantEvents() {
        this.optionInputs.forEach((input) => {
            input.addEventListener('change', () => {
                this.handleVariantChange();
            });
        });
    }


    getSelectedOptions() {
        const selected = [];

        const positions = new Set(
            this.optionInputs.map((input) =>
                Number.parseInt(
                    input.dataset.optionPosition,
                    10
                )
            )
        );

        Array.from(positions)
            .sort((a, b) => a - b)
            .forEach((position) => {
                const checked = this.section.querySelector(
                    `[data-zk-buy-option][data-option-position="${position}"]:checked`
                );

                if (checked) {
                    selected.push(checked.value);
                }
            });

        return selected;
    }


    findVariant() {
        const selectedOptions = this.getSelectedOptions();

        return this.variants.find((variant) => {
            if (!Array.isArray(variant.options)) {
                return false;
            }

            return variant.options.every(
                (option, index) =>
                    option === selectedOptions[index]
            );
        });
    }


    handleVariantChange() {
        const variant = this.findVariant();

        if (!variant) {
            this.updateUnavailableState();
            return;
        }

        if (this.variantInput) {
            this.variantInput.value = variant.id;
        }

        this.updatePrice(variant);
        this.updateAvailability(variant);
        this.updateUrl(variant);
    }


    /* ==========================================================================
       Price
       ========================================================================== */

    updatePrice(variant) {
        if (this.currentPrice) {
            this.currentPrice.textContent =
                this.formatMoney(variant.price);
        }

        if (!this.comparePrice) {
            return;
        }

        if (
            variant.compare_at_price &&
            variant.compare_at_price > variant.price
        ) {
            this.comparePrice.textContent =
                this.formatMoney(
                    variant.compare_at_price
                );

            this.comparePrice.classList.remove(
                'is-hidden'
            );
        } else {
            this.comparePrice.textContent = '';

            this.comparePrice.classList.add(
                'is-hidden'
            );
        }
    }


    /* ==========================================================================
       Availability
       ========================================================================== */

    updateAvailability(variant) {
        if (this.status) {
            this.status.classList.toggle(
                'is-available',
                variant.available
            );

            this.status.classList.toggle(
                'is-sold-out',
                !variant.available
            );

            this.status.textContent =
                variant.available
                    ? 'In stock'
                    : 'Sold out';
        }

        if (this.addButton) {
            this.addButton.disabled =
                !variant.available;
        }

        if (this.addLabel) {
            this.addLabel.textContent =
                variant.available
                    ? 'Add to cart'
                    : 'Sold out';
        }
    }


    updateUnavailableState() {
        if (this.status) {
            this.status.classList.remove(
                'is-available'
            );

            this.status.classList.add(
                'is-sold-out'
            );

            this.status.textContent =
                'Unavailable';
        }

        if (this.addButton) {
            this.addButton.disabled = true;
        }

        if (this.addLabel) {
            this.addLabel.textContent =
                'Unavailable';
        }
    }


    /* ==========================================================================
       URL
       ========================================================================== */

    updateUrl(variant) {
        if (
            !variant ||
            !window.history.replaceState
        ) {
            return;
        }

        const url = new URL(
            window.location.href
        );

        url.searchParams.set(
            'variant',
            variant.id
        );

        window.history.replaceState(
            {},
            '',
            url.toString()
        );
    }


    /* ==========================================================================
       Money Formatting
       ========================================================================== */

    formatMoney(cents) {
        if (
            window.Shopify &&
            typeof Shopify.formatMoney === 'function'
        ) {
            return Shopify.formatMoney(cents);
        }

        const amount =
            Number(cents) / 100;

        return amount.toLocaleString(
            undefined,
            {
                style: 'currency',
                currency:
                    window.Shopify?.currency?.active ||
                    'USD',
            }
        );
    }


    /* ==========================================================================
       Gallery
       ========================================================================== */

    initGallery() {
        this.gallery =
            this.section.querySelector(
                '[data-zk-buy-gallery]'
            );

        if (!this.gallery) {
            return;
        }

        this.galleryTrack =
            this.gallery.querySelector(
                '[data-zk-buy-gallery-track]'
            );

        this.gallerySlides =
            Array.from(
                this.gallery.querySelectorAll(
                    '[data-zk-buy-gallery-slide]'
                )
            );

        this.galleryDots =
            Array.from(
                this.gallery.querySelectorAll(
                    '[data-zk-buy-gallery-dot]'
                )
            );

        if (
            !this.galleryTrack ||
            this.gallerySlides.length <= 1
        ) {
            return;
        }

        this.galleryIndex = 0;
        this.galleryPhysicalIndex = 1;
        this.galleryAutoplayEnabled = true;
        this.galleryInterval = Number.parseInt(this.section.dataset.galleryInterval, 10) || 5000;
        this.galleryReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.galleryTimer = null;

        this.galleryDragging = false;
        this.galleryStartX = 0;
        this.galleryDistance = 0;
        this.galleryPointerId = null;

        this.bindGalleryPagination();
        this.bindGalleryPointerEvents();
        this.setupGalleryLoop();
        this.bindGalleryAutoplay();

        this.galleryTrack.addEventListener('transitionend', () => {
            if (this.galleryPhysicalIndex === 0) {
                this.galleryPhysicalIndex = this.gallerySlides.length;
                this.renderGallery(false);
            } else if (this.galleryPhysicalIndex === this.gallerySlides.length + 1) {
                this.galleryPhysicalIndex = 1;
                this.renderGallery(false);
            }
        });

        this.goToGallery(0, false);
        this.startGalleryAutoplay();
    }


    bindGalleryPagination() {
        this.galleryDots.forEach((dot) => {
            dot.addEventListener(
                'click',
                () => {
                    const index =
                        Number.parseInt(
                            dot.dataset.slideIndex,
                            10
                        );

                    if (!Number.isNaN(index)) {
                        this.goToGallery(index);
                        this.restartGalleryAutoplay();
                    }
                }
            );
        });
    }


    bindGalleryPointerEvents() {
        this.gallery.addEventListener(
            'pointerdown',
            (event) => {
                this.galleryPointerDown(event);
            }
        );

        this.gallery.addEventListener(
            'pointermove',
            (event) => {
                this.galleryPointerMove(event);
            },
            { passive: false }
        );

        this.gallery.addEventListener(
            'pointerup',
            (event) => {
                this.galleryPointerUp(event);
            }
        );

        this.gallery.addEventListener(
            'pointercancel',
            (event) => {
                this.galleryPointerUp(event);
            }
        );
    }


    galleryPointerDown(event) {
        if (
            event.pointerType === 'mouse' &&
            event.button !== 0
        ) {
            return;
        }

        if (
            event.target.closest(
                '[data-zk-buy-gallery-dot], button, video, iframe'
            )
        ) {
            return;
        }

        this.galleryDragging = true;

        this.galleryStartX =
            event.clientX;

        this.galleryDistance = 0;

        this.galleryPointerId =
            event.pointerId;

        this.gallery.classList.add(
            'is-dragging'
        );

        this.stopGalleryAutoplay();

        if (
            typeof this.gallery.setPointerCapture ===
            'function'
        ) {
            try {
                this.gallery.setPointerCapture(
                    event.pointerId
                );
            } catch (error) {
                // Pointer capture is optional.
            }
        }
    }


    galleryPointerMove(event) {
        if (!this.galleryDragging) {
            return;
        }

        if (
            this.galleryPointerId !== null &&
            event.pointerId !==
            this.galleryPointerId
        ) {
            return;
        }

        this.galleryDistance =
            event.clientX -
            this.galleryStartX;

        if (
            Math.abs(this.galleryDistance) >
            8
        ) {
            event.preventDefault();
        }

        const width =
            this.gallery.clientWidth;

        if (!width) {
            return;
        }

        const basePercent =
            -(this.galleryPhysicalIndex * 100);

        const dragPercent =
            (this.galleryDistance / width) *
            100;

        this.galleryTrack.style.transform =
            `translate3d(${basePercent + dragPercent
            }%, 0, 0)`;
    }


    galleryPointerUp(event) {
        if (!this.galleryDragging) {
            return;
        }

        if (
            this.galleryPointerId !== null &&
            event.pointerId !==
            this.galleryPointerId
        ) {
            return;
        }

        this.galleryDragging = false;
        this.galleryPointerId = null;

        this.gallery.classList.remove(
            'is-dragging'
        );

        const threshold =
            Math.min(
                Math.max(
                    this.gallery.clientWidth *
                    0.15,
                    48
                ),
                120
            );

        if (
            this.galleryDistance <=
            -threshold
        ) {
            this.nextGallerySlide();
        } else if (
            this.galleryDistance >=
            threshold
        ) {
            this.previousGallerySlide();
        } else {
            this.goToGallery(
                this.galleryIndex
            );
        }

        this.galleryDistance = 0;
        this.startGalleryAutoplay();
    }


    nextGallerySlide() {
        this.galleryIndex = (this.galleryIndex + 1) % this.gallerySlides.length;
        this.galleryPhysicalIndex += 1;
        this.renderGallery();
    }


    previousGallerySlide() {
        this.galleryIndex = (this.galleryIndex - 1 + this.gallerySlides.length) % this.gallerySlides.length;
        this.galleryPhysicalIndex -= 1;
        this.renderGallery();
    }


    goToGallery(
        index,
        animate = true
    ) {
        if (
            index < 0 ||
            index >= this.gallerySlides.length
        ) {
            return;
        }

        this.galleryIndex = index;
        this.galleryPhysicalIndex = index + 1;

        this.renderGallery(animate);
    }

    setupGalleryLoop() {
        const first = this.gallerySlides[0].cloneNode(true);
        const last = this.gallerySlides[this.gallerySlides.length - 1].cloneNode(true);
        [first, last].forEach((clone) => {
            clone.dataset.zkClone = 'true';
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('a, button, input, select, textarea, video, iframe').forEach((item) => item.setAttribute('tabindex', '-1'));
        });
        this.galleryTrack.prepend(last);
        this.galleryTrack.append(first);
    }

    renderGallery(animate = true) {

        if (!animate) {
            this.galleryTrack.style.transition =
                'none';
        } else {
            this.galleryTrack.style.removeProperty(
                'transition'
            );
        }

        this.galleryTrack.style.transform =
            `translate3d(-${this.galleryPhysicalIndex * 100
            }%, 0, 0)`;

        this.gallerySlides.forEach(
            (slide, slideIndex) => {
                const active =
                    slideIndex === this.galleryIndex;

                slide.classList.toggle(
                    'is-active',
                    active
                );

                slide.setAttribute(
                    'aria-hidden',
                    active
                        ? 'false'
                        : 'true'
                );
            }
        );

        this.galleryDots.forEach(
            (dot, dotIndex) => {
                const active =
                    dotIndex === this.galleryIndex;

                dot.classList.toggle(
                    'is-active',
                    active
                );

                if (active) {
                    dot.setAttribute(
                        'aria-current',
                        'true'
                    );
                } else {
                    dot.removeAttribute(
                        'aria-current'
                    );
                }
            }
        );

        if (!animate) {
            requestAnimationFrame(
                () => {
                    this.galleryTrack.style.removeProperty(
                        'transition'
                    );
                }
            );
        }
    }

    bindGalleryAutoplay() {
        this.gallery.addEventListener('focusin', () => this.stopGalleryAutoplay());
        this.gallery.addEventListener('focusout', (event) => {
            if (!this.gallery.contains(event.relatedTarget)) this.startGalleryAutoplay();
        });
        this.gallery.addEventListener('dragstart', (event) => event.preventDefault());
        document.addEventListener('visibilitychange', () => {
            document.hidden ? this.stopGalleryAutoplay() : this.startGalleryAutoplay();
        });
    }

    startGalleryAutoplay() {
        if (!this.galleryAutoplayEnabled || this.galleryReducedMotion.matches || document.hidden || this.galleryDragging) return;
        this.stopGalleryAutoplay();
        this.galleryTimer = window.setInterval(() => this.nextGallerySlide(), this.galleryInterval);
    }

    stopGalleryAutoplay() {
        if (this.galleryTimer) window.clearInterval(this.galleryTimer);
        this.galleryTimer = null;
    }

    restartGalleryAutoplay() {
        this.stopGalleryAutoplay();
        this.startGalleryAutoplay();
    }
}


/* ==========================================================================
   Initialization
   ========================================================================== */

const zerokitBuySections =
    new WeakMap();


function initZerokitBuySections(
    scope = document
) {
    const sections =
        scope.matches?.(
            '[data-zk-buy-section]'
        )
            ? [scope]
            : Array.from(
                scope.querySelectorAll?.(
                    '[data-zk-buy-section]'
                ) || []
            );

    sections.forEach((section) => {
        if (
            zerokitBuySections.has(section)
        ) {
            return;
        }

        zerokitBuySections.set(
            section,
            new ZerokitProductBuy(section)
        );
    });
}


if (
    document.readyState ===
    'loading'
) {
    document.addEventListener(
        'DOMContentLoaded',
        () => {
            initZerokitBuySections();
        }
    );
} else {
    initZerokitBuySections();
}


/* ==========================================================================
   Shopify Theme Editor
   ========================================================================== */

document.addEventListener(
    'shopify:section:load',
    (event) => {
        initZerokitBuySections(
            event.target
        );
    }
);
